import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { ShieldCheck } from 'lucide-react';

const PrivacyPage = () => {
  const { language, t } = useLanguage();

  return (
    <div className="min-h-screen bg-gray-50" data-testid="privacy-page">
      <SEO
        title={t('Privacybeleid - U&Fashion', 'Privacy Policy - U&Fashion')}
        description={t(
          'Hoe U&Fashion uw persoonsgegevens verwerkt — GDPR-conform.',
          'How U&Fashion processes your personal data — GDPR-compliant.'
        )}
      />

      <div className="bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-4xl font-bold mb-4">
            {t('Privacybeleid', 'Privacy Policy')}
          </h1>
          <p className="text-xl text-white/90">
            {t(
              'Hoe wij omgaan met uw persoonsgegevens',
              'How we handle your personal data'
            )}
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-xl shadow-md p-8 space-y-8">
          {language === 'nl' ? (
            <>
              {/* Intro card */}
              <div className="bg-gradient-to-r from-pink-50 to-purple-50 p-6 rounded-lg flex items-start gap-4">
                <ShieldCheck className="w-8 h-8 flex-shrink-0 text-purple-600 mt-1" />
                <div>
                  <p className="text-lg text-gray-800 leading-relaxed">
                    Bij U&Fashion respecteren wij uw privacy. Dit beleid legt uit welke gegevens we verzamelen,
                    waarvoor we ze gebruiken en welke rechten u heeft onder de Algemene Verordening Gegevensbescherming (AVG/GDPR).
                  </p>
                </div>
              </div>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Verwerkingsverantwoordelijke</h2>
                <p className="text-gray-700 leading-relaxed">
                  De verwerkingsverantwoordelijke voor uw persoonsgegevens is U&Fashion.eu.
                  Voor vragen of verzoeken kan u ons bereiken via{' '}
                  <a href="mailto:info@uandfashion.eu" className="text-purple-600 underline hover:text-purple-800">
                    info@uandfashion.eu
                  </a>.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Welke gegevens verzamelen we?</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  We verzamelen enkel de gegevens die nodig zijn voor de werking van onze website en onze nieuwsbrief:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-2">
                  <li><strong>Nieuwsbrief:</strong> uw e-mailadres en taalvoorkeur (NL/EN), enkel wanneer u zich uitdrukkelijk inschrijft.</li>
                  <li><strong>Toestemming:</strong> tijdstempel van wanneer u toestemming gaf om de nieuwsbrief te ontvangen.</li>
                  <li><strong>Technische gegevens:</strong> standaard server-logs (IP-adres, browser, bezochte pagina's) die we gebruiken voor beveiliging en het beheer van de site. Deze worden maximaal 30 dagen bewaard.</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-3">
                  We vragen of verwerken <strong>geen</strong> betaalgegevens, adressen of telefoonnummers. Aankopen verlopen rechtstreeks via de partner-retailers waar u terechtkomt na het klikken op een affiliate link.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Waarvoor gebruiken we uw gegevens?</h2>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-2">
                  <li>Om u onze nieuwsbrief te bezorgen met fashion-acties, seizoenstrends en merkverhalen.</li>
                  <li>Om de website veilig en functioneel te houden.</li>
                  <li>Om te voldoen aan onze wettelijke verplichtingen.</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-3">
                  We gebruiken uw e-mailadres <strong>niet</strong> voor andere doeleinden, en we verkopen het <strong>nooit</strong> aan derden.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Rechtsgrond</h2>
                <p className="text-gray-700 leading-relaxed">
                  De verwerking van uw e-mailadres voor de nieuwsbrief gebeurt op basis van uw <strong>uitdrukkelijke toestemming</strong> (artikel 6.1.a AVG), die u te allen tijde kan intrekken.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Hoe lang bewaren we uw gegevens?</h2>
                <p className="text-gray-700 leading-relaxed">
                  Uw e-mailadres bewaren wij zolang u ingeschreven bent op onze nieuwsbrief. Na uitschrijving verwijderen we uw gegevens binnen 30 dagen, behalve waar de wet ons verplicht om bepaalde gegevens langer te bewaren.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Met wie delen we uw gegevens?</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Uw gegevens worden enkel gedeeld met technische verwerkers die voor ons werken:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-2">
                  <li><strong>Onze hostingprovider</strong> (binnen de EU) voor het opslaan van inschrijvingen.</li>
                  <li><strong>E-mailservice</strong> (Zoho Mail / Zoho Campaigns) voor het verzenden van de nieuwsbrief.</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-3">
                  Wij verkopen, verhuren of geven uw gegevens nooit door aan derden voor marketingdoeleinden.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Affiliate-links en cookies van partners</h2>
                <p className="text-gray-700 leading-relaxed">
                  U&Fashion bevat affiliate-links naar externe retailers (zoals Awin, Tradetracker, Tradedoubler). Wanneer u op een product klikt en doorklikt naar de partner-retailer, kunnen die partijen cookies plaatsen om hun affiliate-programma te laten werken. Deze cookies vallen onder het privacybeleid van de partner-retailer in kwestie, niet onder dat van U&Fashion.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Uw rechten onder de AVG</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  U heeft op elk moment de volgende rechten met betrekking tot uw persoonsgegevens:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-2">
                  <li><strong>Recht op inzage</strong> — opvragen welke gegevens we van u hebben.</li>
                  <li><strong>Recht op rectificatie</strong> — onjuiste gegevens laten corrigeren.</li>
                  <li><strong>Recht op verwijdering</strong> ("vergetelheid") — uw gegevens laten wissen.</li>
                  <li><strong>Recht op beperking</strong> van de verwerking.</li>
                  <li><strong>Recht op overdraagbaarheid</strong> — uw gegevens in een gestructureerd formaat ontvangen.</li>
                  <li><strong>Recht op bezwaar</strong> tegen de verwerking.</li>
                  <li><strong>Recht om uw toestemming in te trekken</strong> op elk moment — u kan zich uitschrijven via de link onderaan elke nieuwsbrief of door ons te mailen.</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-3">
                  Om één van deze rechten uit te oefenen, stuur een mail naar{' '}
                  <a href="mailto:info@uandfashion.eu" className="text-purple-600 underline hover:text-purple-800">
                    info@uandfashion.eu
                  </a>. We reageren binnen 30 dagen.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Klacht indienen</h2>
                <p className="text-gray-700 leading-relaxed">
                  Bent u niet tevreden met de manier waarop wij met uw gegevens omgaan? Dan heeft u het recht een klacht in te dienen bij de{' '}
                  <a
                    href="https://www.gegevensbeschermingsautoriteit.be"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-600 underline hover:text-purple-800"
                  >
                    Belgische Gegevensbeschermingsautoriteit (GBA)
                  </a>
                  , Drukpersstraat 35, 1000 Brussel.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Beveiliging</h2>
                <p className="text-gray-700 leading-relaxed">
                  We nemen passende technische en organisatorische maatregelen om uw gegevens te beveiligen tegen ongeoorloofde toegang, verlies of misbruik. Onze website maakt gebruik van HTTPS-versleuteling.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Wijzigingen aan dit beleid</h2>
                <p className="text-gray-700 leading-relaxed">
                  We kunnen dit privacybeleid van tijd tot tijd aanpassen. De meest recente versie is altijd op deze pagina te raadplegen. Bij ingrijpende wijzigingen brengen we u op de hoogte via e-mail (als u ingeschreven bent) of via een melding op de website.
                </p>
              </section>

              <div className="bg-gray-100 p-4 rounded-lg text-sm text-gray-600">
                <p><strong>Laatst bijgewerkt:</strong> 30 mei 2026</p>
              </div>

              <div className="text-center pt-4">
                <Link
                  to="/"
                  className="inline-block bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all"
                  data-testid="privacy-back-home"
                >
                  ← Terug naar de homepagina
                </Link>
              </div>
            </>
          ) : (
            <>
              {/* English Version */}
              <div className="bg-gradient-to-r from-pink-50 to-purple-50 p-6 rounded-lg flex items-start gap-4">
                <ShieldCheck className="w-8 h-8 flex-shrink-0 text-purple-600 mt-1" />
                <div>
                  <p className="text-lg text-gray-800 leading-relaxed">
                    At U&Fashion we respect your privacy. This policy explains what data we collect,
                    what we use it for, and what rights you have under the General Data Protection Regulation (GDPR).
                  </p>
                </div>
              </div>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Data Controller</h2>
                <p className="text-gray-700 leading-relaxed">
                  The data controller for your personal data is U&Fashion.eu.
                  For questions or requests please contact us at{' '}
                  <a href="mailto:info@uandfashion.eu" className="text-purple-600 underline hover:text-purple-800">
                    info@uandfashion.eu
                  </a>.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">2. What data do we collect?</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  We only collect data needed to operate our website and newsletter:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-2">
                  <li><strong>Newsletter:</strong> your email address and language preference (NL/EN), only when you explicitly subscribe.</li>
                  <li><strong>Consent:</strong> the timestamp of when you gave consent to receive the newsletter.</li>
                  <li><strong>Technical data:</strong> standard server logs (IP address, browser, visited pages) used for security and site administration. Kept for a maximum of 30 days.</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-3">
                  We do <strong>not</strong> request or process payment data, addresses or phone numbers. Purchases happen directly through the partner retailers you reach after clicking an affiliate link.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How do we use your data?</h2>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-2">
                  <li>To deliver our newsletter with fashion deals, seasonal trends and brand stories.</li>
                  <li>To keep the website secure and functional.</li>
                  <li>To comply with our legal obligations.</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-3">
                  We do <strong>not</strong> use your email for any other purpose, and we <strong>never</strong> sell it to third parties.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Legal basis</h2>
                <p className="text-gray-700 leading-relaxed">
                  The processing of your email address for the newsletter is based on your <strong>explicit consent</strong> (article 6.1.a GDPR), which you can withdraw at any time.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">5. How long do we keep your data?</h2>
                <p className="text-gray-700 leading-relaxed">
                  We keep your email address as long as you remain subscribed to our newsletter. After unsubscribing we delete your data within 30 days, except where law requires us to retain certain data for longer.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Who do we share your data with?</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Your data is only shared with technical processors acting on our behalf:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-2">
                  <li><strong>Our hosting provider</strong> (within the EU) for storing subscriptions.</li>
                  <li><strong>Email service</strong> (Zoho Mail / Zoho Campaigns) for sending the newsletter.</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-3">
                  We never sell, rent or share your data with third parties for marketing purposes.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Affiliate links and partner cookies</h2>
                <p className="text-gray-700 leading-relaxed">
                  U&Fashion contains affiliate links to external retailers (such as Awin, Tradetracker, Tradedoubler). When you click a product and proceed to the partner retailer, those parties may set cookies to operate their affiliate program. These cookies fall under the privacy policy of the partner retailer concerned, not under U&Fashion's policy.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Your rights under GDPR</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  You have the following rights regarding your personal data at any time:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-2">
                  <li><strong>Right of access</strong> — request what data we hold about you.</li>
                  <li><strong>Right to rectification</strong> — have incorrect data corrected.</li>
                  <li><strong>Right to erasure</strong> ("right to be forgotten") — have your data deleted.</li>
                  <li><strong>Right to restrict</strong> processing.</li>
                  <li><strong>Right to data portability</strong> — receive your data in a structured format.</li>
                  <li><strong>Right to object</strong> to processing.</li>
                  <li><strong>Right to withdraw your consent</strong> at any time — you can unsubscribe via the link at the bottom of every newsletter, or by emailing us.</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-3">
                  To exercise any of these rights, send an email to{' '}
                  <a href="mailto:info@uandfashion.eu" className="text-purple-600 underline hover:text-purple-800">
                    info@uandfashion.eu
                  </a>. We respond within 30 days.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Filing a complaint</h2>
                <p className="text-gray-700 leading-relaxed">
                  If you are not satisfied with how we handle your data, you have the right to lodge a complaint with the{' '}
                  <a
                    href="https://www.dataprotectionauthority.be"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-600 underline hover:text-purple-800"
                  >
                    Belgian Data Protection Authority
                  </a>
                  , Drukpersstraat 35, 1000 Brussels.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Security</h2>
                <p className="text-gray-700 leading-relaxed">
                  We take appropriate technical and organisational measures to protect your data against unauthorised access, loss or misuse. Our website uses HTTPS encryption.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Changes to this policy</h2>
                <p className="text-gray-700 leading-relaxed">
                  We may update this privacy policy from time to time. The most recent version is always available on this page. For substantial changes we will notify you by email (if subscribed) or via a notice on the website.
                </p>
              </section>

              <div className="bg-gray-100 p-4 rounded-lg text-sm text-gray-600">
                <p><strong>Last updated:</strong> May 30, 2026</p>
              </div>

              <div className="text-center pt-4">
                <Link
                  to="/"
                  className="inline-block bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all"
                  data-testid="privacy-back-home"
                >
                  ← Back to homepage
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
